import { useState } from 'react';
import { App, Dropdown, type MenuProps } from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FileAddOutlined,
  FileTextOutlined,
  FolderAddOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  RightOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';
import type { Collection } from '@core/entities/Collection';
import type { ExportFormat } from '@core/repositories/CollectionRepository';
import { NamePromptModal } from '@presentation/components/workbench/NamePromptModal';
import { FolderTreeLevel } from '@presentation/components/workbench/FolderTreeLevel';
import { CollectionDocsModal } from '@presentation/components/workbench/CollectionDocsModal';
import { CollectionRunnerDrawer } from '@presentation/components/workbench/CollectionRunnerDrawer';
import {
  useDeleteCollection,
  useDuplicateCollection,
  useExportCollection,
  useToggleCollectionFavorite,
  useUpdateCollection,
} from '@presentation/hooks/useCollections';
import { useCreateFolder } from '@presentation/hooks/useFolders';
import { useWorkbenchStore } from '@presentation/stores/workbenchStore';

interface CollectionTreeNodeProps {
  workspaceId: string;
  collection: Collection;
}

export const CollectionTreeNode = ({ workspaceId, collection }: CollectionTreeNodeProps) => {
  const { message, modal } = App.useApp();
  const [expanded, setExpanded] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [runnerOpen, setRunnerOpen] = useState(false);
  const openNewRequestTab = useWorkbenchStore((state) => state.openNewRequestTab);
  const updateCollection = useUpdateCollection(collection.id, workspaceId);
  const duplicateCollection = useDuplicateCollection(workspaceId);
  const deleteCollection = useDeleteCollection(workspaceId);
  const toggleFavorite = useToggleCollectionFavorite(workspaceId);
  const createFolder = useCreateFolder(collection.id);
  const exportCollection = useExportCollection();

  const menuItems: MenuProps['items'] = [
    { key: 'new-request', icon: <FileAddOutlined />, label: 'New request' },
    { key: 'new-folder', icon: <FolderAddOutlined />, label: 'New folder' },
    { key: 'run', icon: <PlayCircleOutlined />, label: 'Run collection' },
    {
      key: 'favorite',
      icon: collection.isFavorited ? <StarFilled className="text-amber-400" /> : <StarOutlined />,
      label: collection.isFavorited ? 'Remove from favorites' : 'Add to favorites',
    },
    { key: 'rename', icon: <EditOutlined />, label: 'Rename' },
    { key: 'docs', icon: <FileTextOutlined />, label: 'Documentation' },
    { key: 'duplicate', icon: <CopyOutlined />, label: 'Duplicate' },
    {
      key: 'export',
      icon: <DownloadOutlined />,
      label: 'Export',
      children: [
        { key: 'export-postman', label: 'Postman Collection (.json)' },
        { key: 'export-curl', label: 'cURL script (.sh)' },
      ],
    },
    { type: 'divider' },
    { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true },
  ];

  const handleExport = (format: ExportFormat) => {
    exportCollection.mutate(
      { id: collection.id, format },
      {
        onSuccess: ({ blob, filename }) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          void message.success(`Exported "${collection.name}"`);
        },
        onError: (error) => void message.error(error.message || 'Export failed'),
      },
    );
  };

  const handleMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
    domEvent.stopPropagation();
    if (key === 'new-request') {
      openNewRequestTab(workspaceId, collection.id, null);
    } else if (key === 'new-folder') {
      setCreatingFolder(true);
    } else if (key === 'run') {
      setRunnerOpen(true);
    } else if (key === 'favorite') {
      toggleFavorite.mutate({ id: collection.id, isFavorited: collection.isFavorited });
    } else if (key === 'rename') {
      setRenaming(true);
    } else if (key === 'docs') {
      setDocsOpen(true);
    } else if (key === 'duplicate') {
      duplicateCollection.mutate(collection.id, {
        onSuccess: () => message.success('Collection duplicated'),
      });
    } else if (key === 'export-postman') {
      handleExport('postman');
    } else if (key === 'export-curl') {
      handleExport('curl');
    } else if (key === 'delete') {
      modal.confirm({
        title: `Delete "${collection.name}"?`,
        content: 'This deletes every folder and request inside it. This cannot be undone.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: () =>
          deleteCollection.mutate(collection.id, {
            onSuccess: () => message.success('Collection deleted'),
          }),
      });
    }
  };

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((value) => !value)}
        className="group flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5"
      >
        <RightOutlined
          className={`shrink-0 text-[10px] transition-transform ${expanded ? 'rotate-90' : ''}`}
        />
        {expanded ? (
          <FolderOpenOutlined className="shrink-0" />
        ) : (
          <FolderOutlined className="shrink-0" />
        )}
        <span className="min-w-0 flex-1 truncate">{collection.name}</span>
        {collection.isFavorited && <StarFilled className="shrink-0 text-[11px] text-amber-400" />}
        <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={['click']}>
          <button
            type="button"
            onClick={(event) => event.stopPropagation()}
            className="shrink-0 rounded px-1 opacity-0 group-hover:opacity-100"
            aria-label="Collection actions"
          >
            <MoreOutlined />
          </button>
        </Dropdown>
      </div>

      {expanded && (
        <FolderTreeLevel
          workspaceId={workspaceId}
          collectionId={collection.id}
          parentId={null}
          depth={1}
        />
      )}

      <NamePromptModal
        open={renaming}
        title="Rename collection"
        initialValue={collection.name}
        confirmLoading={updateCollection.isPending}
        onCancel={() => setRenaming(false)}
        onSubmit={(name) =>
          updateCollection.mutate({ name }, { onSuccess: () => setRenaming(false) })
        }
      />

      <NamePromptModal
        open={creatingFolder}
        title="New folder"
        confirmLoading={createFolder.isPending}
        onCancel={() => setCreatingFolder(false)}
        onSubmit={(name) =>
          createFolder.mutate({ name }, { onSuccess: () => setCreatingFolder(false) })
        }
      />

      <CollectionDocsModal
        workspaceId={workspaceId}
        collection={collection}
        open={docsOpen}
        onClose={() => setDocsOpen(false)}
      />

      <CollectionRunnerDrawer
        collectionId={collection.id}
        collectionName={collection.name}
        open={runnerOpen}
        onClose={() => setRunnerOpen(false)}
      />
    </div>
  );
};
